import React, { useState } from 'react';
import { useAuthStore, getAuthAxios } from '@/store';
import { useI18n } from '@/components/I18nProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { User, Mail, Shield, Building2, Save, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user, setUser } = useAuthStore();
  const { t } = useI18n();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', newPass: '', confirm: '' });
  const [changingPass, setChangingPass] = useState(false);

  const roleLabels = { super_admin: 'Süper Admin', company_admin: t('companyAdmin'), personnel: t('rolePersonnel'), dealer: 'Bayi' };
  const getInitials = (n) => n?.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const handleSaveName = async () => {
    if (!name.trim()) return toast.error(t('nameEmpty'));
    setSaving(true);
    try {
      const api = getAuthAxios();
      const res = await api.put('/auth/profile', { name: name.trim() });
      setUser({ ...user, name: res.data.name });
      toast.success(t('profileUpdated'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('profileUpdateFailed'));
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.newPass) return toast.error(t('allFieldsRequired'));
    if (passwords.newPass.length < 8) return toast.error(t('passwordMinLength'));
    if (!/[A-Z]/.test(passwords.newPass)) return toast.error(t('passwordNeedUpper'));
    if (!/[a-z]/.test(passwords.newPass)) return toast.error(t('passwordNeedLower'));
    if (!/[0-9]/.test(passwords.newPass)) return toast.error(t('passwordNeedDigit'));
    if (passwords.newPass !== passwords.confirm) return toast.error(t('newPasswordsNotMatch'));
    setChangingPass(true);
    try {
      const api = getAuthAxios();
      await api.put('/auth/change-password', { current_password: passwords.current, new_password: passwords.newPass });
      toast.success(t('passwordChanged'));
      setPasswords({ current: '', newPass: '', confirm: '' });
      setTimeout(() => { useAuthStore.getState().logout(); }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('passwordChangeFailed'));
    }
    setChangingPass(false);
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 animate-fade-in" data-testid="profile-page">
      <div>
        <h1 className="font-heading text-2xl font-bold sm:text-3xl">{t('profileTitle')}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t('profileDesc')}</p>
      </div>
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarFallback className="bg-primary/10 text-primary text-xl">{getInitials(user?.name)}</AvatarFallback>
            </Avatar>
            <div>
              <CardTitle>{user?.name}</CardTitle>
              <CardDescription className="flex items-center gap-2 mt-1"><Mail className="h-3.5 w-3.5" />{user?.email}</CardDescription>
              <Badge className="mt-2" variant="secondary"><Shield className="mr-1 h-3 w-3" />{roleLabels[user?.role] || user?.role}</Badge>
            </div>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('fullName')}</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <Button onClick={handleSaveName} disabled={saving} data-testid="save-profile-btn">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            {t('save')}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Lock className="h-5 w-5 text-primary" />{t('changePassword')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{t('currentPassword')}</Label>
            <Input type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} data-testid="current-password" />
          </div>
          <div className="space-y-2">
            <Label>{t('newPassword')}</Label>
            <Input type="password" value={passwords.newPass} onChange={(e) => setPasswords({ ...passwords, newPass: e.target.value })} data-testid="new-password" />
          </div>
          <div className="space-y-2">
            <Label>{t('confirmNewPassword')}</Label>
            <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} data-testid="confirm-password" />
          </div>
          <Button onClick={handleChangePassword} disabled={changingPass} data-testid="change-password-btn">
            {changingPass ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Lock className="mr-2 h-4 w-4" />}
            {t('changePassword')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
